import { Activity, Bug, ChevronUp, Filter, RotateCw } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { FeedEvent } from "../../../shared/types";
import { EVENT_ORDER, metaFor } from "../../components/EventTypeIcon";
import { FeedItem } from "../../components/FeedItem";
import { MetricsStrip } from "../../components/MetricsStrip";
import { SweepBanner } from "../../components/SweepBanner";
import { Button, EmptyState, Page, SectionTitle } from "../../ui";
import { SkeletonList } from "../../ui/Skeleton";
import { useWs } from "../../ws";
import { NeedsYou } from "./NeedsYou";

const PAGE_SIZE = 50;
const MAX_KEPT = 500;
const FRESH_HIGHLIGHT_MS = 4_000;
const SCROLL_AWAY_PX = 240;

async function fetchFeed(before?: number): Promise<FeedEvent[]> {
	const qs = new URLSearchParams({ limit: String(PAGE_SIZE) });
	if (before != null) qs.set("before", String(before));
	const res = await fetch(`/api/feed?${qs}`);
	const data = (await res.json()) as { events: FeedEvent[] };
	return data.events;
}

async function postDebug(): Promise<void> {
	await fetch("/api/feed/debug", {
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify({
			type: "debug",
			payload: {
				storeId: "dev",
				title: `Synthetic event @ ${new Date().toLocaleTimeString()}`,
				message: "Posted from Now page debug button",
			},
		}),
	});
}

function payloadStoreId(e: FeedEvent): string | null {
	const p = e.payload as { storeId?: string } | null;
	return p?.storeId ?? null;
}

function dedupePrepend(incoming: FeedEvent[], existing: FeedEvent[]): FeedEvent[] {
	const seen = new Set<number>();
	const out: FeedEvent[] = [];
	for (const e of incoming) {
		if (seen.has(e.id)) continue;
		seen.add(e.id);
		out.push(e);
	}
	for (const e of existing) {
		if (seen.has(e.id)) continue;
		seen.add(e.id);
		out.push(e);
	}
	return out.slice(0, MAX_KEPT);
}

/**
 * The Now screen, in the order the operator's daily jobs need it:
 *
 * 1. Needs you — buy jobs + live restocks, above the fold.
 * 2. Health — the figures and the per-store sweep list, quiet when fine.
 * 3. Stream — the event history and filters that used to live at /feed.
 *
 * On a phone that is the reading order top to bottom. From lg up the stream
 * takes the width it needs to be one line per event, and Needs you + Health
 * ride beside it in a sticky column so they stay in view while it scrolls.
 */
export function Now() {
	return (
		<Page>
			<div className="grid gap-x-5 gap-y-4 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px]">
				<aside className="flex flex-col gap-4 lg:sticky lg:top-16 lg:col-start-2 lg:row-start-1 lg:max-h-[calc(100vh-5rem)] lg:self-start lg:overflow-y-auto">
					<section>
						<SectionTitle>Needs you</SectionTitle>
						<NeedsYou />
					</section>
					<section>
						<SectionTitle>Health</SectionTitle>
						<MetricsStrip />
					</section>
				</aside>

				<div className="min-w-0 lg:col-start-1 lg:row-start-1">
					<Stream />
				</div>
			</div>
		</Page>
	);
}

function Stream() {
	const [events, setEvents] = useState<FeedEvent[]>([]);
	const [pending, setPending] = useState<FeedEvent[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
	const [storeFilter, setStoreFilter] = useState<string>("");
	const [freshIds, setFreshIds] = useState<Set<number>>(new Set());
	const [scrolledAway, setScrolledAway] = useState(false);

	const scrolledAwayRef = useRef(false);
	const knownIdsRef = useRef<Set<number>>(new Set());
	const listRef = useRef<HTMLUListElement>(null);
	const listHeightRef = useRef(0);
	const firstIdRef = useRef<number | null>(null);

	// Initial load
	useEffect(() => {
		fetchFeed()
			.then((e) => {
				setEvents(e);
				knownIdsRef.current = new Set(e.map((x) => x.id));
				setHasMore(e.length === PAGE_SIZE);
			})
			.finally(() => setLoading(false));
	}, []);

	// Track scroll to gate live appends with a "N new" pill
	useEffect(() => {
		const onScroll = () => {
			const away = window.scrollY > SCROLL_AWAY_PX;
			if (away !== scrolledAwayRef.current) {
				scrolledAwayRef.current = away;
				setScrolledAway(away);
			}
		};
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	// Auto-clear fresh markers
	useEffect(() => {
		if (freshIds.size === 0) return;
		const t = setTimeout(() => setFreshIds(new Set()), FRESH_HIGHLIGHT_MS);
		return () => clearTimeout(t);
	}, [freshIds]);

	useWs((msg) => {
		if (msg.type !== "event.new") return;
		const ev = msg.event;
		if (knownIdsRef.current.has(ev.id)) return;
		if (scrolledAwayRef.current) {
			// Buffer the event; the user will see a "N new" pill that prepends them.
			setPending((prev) => (prev.some((e) => e.id === ev.id) ? prev : [ev, ...prev]));
			return;
		}
		knownIdsRef.current.add(ev.id);
		setEvents((prev) => dedupePrepend([ev], prev));
		setFreshIds((prev) => {
			const next = new Set(prev);
			next.add(ev.id);
			return next;
		});
	});

	/**
	 * Hold the viewport still when events land above what you are reading.
	 *
	 * Events prepend, so the list grows upward and everything below the
	 * insertion — including the row you were looking at — slides down by a row.
	 * Measured at 55px per event. Browsers can compensate for this themselves
	 * via scroll anchoring, but it is not happening here, so the growth is
	 * measured and the scroll position moved by exactly as much.
	 *
	 * Only prepends qualify: loading older events appends below the viewport,
	 * where nothing needs holding still.
	 */
	useLayoutEffect(() => {
		const el = listRef.current;
		const firstId = events[0]?.id ?? null;
		const prevFirstId = firstIdRef.current;
		firstIdRef.current = firstId;
		if (!el) return;

		const height = el.offsetHeight;
		const prevHeight = listHeightRef.current;
		listHeightRef.current = height;

		if (prevFirstId === null || firstId === prevFirstId) return;
		if (prevHeight === 0 || height <= prevHeight) return;
		// Nothing to hold still if the list starts below the current position:
		// the new event is arriving on screen where it should be seen.
		const listTop = el.getBoundingClientRect().top + window.scrollY;
		if (window.scrollY <= listTop) return;
		window.scrollBy(0, height - prevHeight);
	}, [events]);

	const flushPending = useCallback(() => {
		if (pending.length > 0) {
			const incoming = [...pending];
			for (const ev of incoming) knownIdsRef.current.add(ev.id);
			setEvents((prev) => dedupePrepend(incoming, prev));
			setFreshIds(new Set(incoming.map((e) => e.id)));
			setPending([]);
		}
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, [pending]);

	const loadOlder = useCallback(async () => {
		if (loadingMore || events.length === 0) return;
		const oldest = events[events.length - 1];
		if (!oldest) return;
		setLoadingMore(true);
		try {
			const more = await fetchFeed(oldest.createdAt);
			const filtered = more.filter((e) => !knownIdsRef.current.has(e.id));
			for (const ev of filtered) knownIdsRef.current.add(ev.id);
			if (filtered.length > 0) {
				setEvents((prev) => [...prev, ...filtered]);
			}
			setHasMore(more.length === PAGE_SIZE);
		} finally {
			setLoadingMore(false);
		}
	}, [events, loadingMore]);

	const debug = useCallback(() => {
		postDebug();
	}, []);

	const knownStores = useMemo(() => {
		const s = new Set<string>();
		for (const e of events) {
			const sid = payloadStoreId(e);
			if (sid) s.add(sid);
		}
		return Array.from(s).sort();
	}, [events]);

	const filtersActive = typeFilter.size > 0 || storeFilter !== "";

	const filtered = useMemo(() => {
		if (!filtersActive) return events;
		return events.filter((e) => {
			if (typeFilter.size > 0 && !typeFilter.has(e.type)) return false;
			if (storeFilter && payloadStoreId(e) !== storeFilter) return false;
			return true;
		});
	}, [events, typeFilter, storeFilter, filtersActive]);

	const toggleType = (t: string) => {
		setTypeFilter((prev) => {
			const next = new Set(prev);
			if (next.has(t)) next.delete(t);
			else next.add(t);
			return next;
		});
	};

	const clearFilters = () => {
		setTypeFilter(new Set());
		setStoreFilter("");
	};

	return (
		<section>
			<header className="mb-2 flex flex-wrap items-center justify-between gap-2">
				<div className="flex min-w-0 items-baseline gap-2.5">
					<h1 className="text-[22px] leading-7 font-bold font-tag">Stream</h1>
					<p className="hidden truncate text-[13px] text-muted sm:block">
						New products, restocks and price changes across every store.
					</p>
				</div>
				{/* Deliberately de-emphasized: a dev-only utility, not a real action. */}
				<Button variant="ghost" size="sm" onClick={debug} className="shrink-0">
					<Bug className="size-3.5" aria-hidden />
					Debug event
				</Button>
			</header>

			<SweepBanner />

			{/* One wrapping row, no card. It used to be a bordered box holding two
          stacked rows whose every control carried a 44px touch floor — about
          130px of chrome above a list of 49px rows. The touch floor is kept
          where fingers are (below sm) and dropped on a pointer device, and
          the store select joins the chips on the same line.

          Each chip keeps its event's icon in its event's hue whether or not
          it is selected, so the row doubles as the legend for the badge
          colours in the feed below it; the tinted fill marks selection. */}
			{/* One line on a phone, scrolling sideways — wrapped, the chips'
          touch floors stacked three rows deep above the first event. */}
			<div className="-mx-3 mb-2 flex [scrollbar-width:none] items-center gap-1 overflow-x-auto px-3 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
				{EVENT_ORDER.map((t) => {
					const meta = metaFor(t);
					const Icon = meta.Icon;
					const active = typeFilter.has(t);
					return (
						<button
							key={t}
							type="button"
							onClick={() => toggleType(t)}
							className={`inline-flex min-h-tap shrink-0 items-center gap-1 rounded-full px-2.5 text-xs whitespace-nowrap transition-colors sm:h-7 sm:min-h-0 ${
								active ? meta.chipActive : "border border-hairline bg-paper text-muted hover:border-line hover:text-ink"
							}`}
							aria-pressed={active}
						>
							<Icon className={`size-3 ${active ? "" : meta.iconColor}`} strokeWidth={2.25} aria-hidden />
							{meta.label}
						</button>
					);
				})}

				<span className="mx-1 hidden h-4 w-px bg-hairline sm:block" aria-hidden />

				<select
					value={storeFilter}
					onChange={(e) => setStoreFilter(e.target.value)}
					className="min-h-tap shrink-0 rounded-full border border-hairline bg-paper px-2.5 text-xs text-ink hover:border-line sm:h-7 sm:min-h-0"
					aria-label="Filter by store"
				>
					<option value="">All stores</option>
					{knownStores.map((s) => (
						<option key={s} value={s}>
							{s}
						</option>
					))}
				</select>

				<span className="ml-1 shrink-0 text-xs whitespace-nowrap text-muted">
					{filtersActive
						? `${filtered.length} of ${events.length}`
						: `${events.length} event${events.length === 1 ? "" : "s"}`}
				</span>

				{filtersActive && (
					<button
						type="button"
						onClick={clearFilters}
						className="min-h-tap text-xs font-medium text-ink hover:underline sm:min-h-0"
					>
						Clear
					</button>
				)}
			</div>

			{/* h-0 so the pill costs no layout. It used to be a plain sticky block,
          which sits in the flow: every time it appeared it pushed the feed
          down and every flush pulled it back up. Sticky rather than fixed so
          it stays centred on the content column instead of the viewport,
          which the sidebar would throw off. */}
			{pending.length > 0 && scrolledAway && (
				<div className="pointer-events-none sticky top-14 z-20 flex h-0 justify-center">
					<button
						type="button"
						onClick={flushPending}
						className="pointer-events-auto inline-flex min-h-tap items-center gap-1.5 rounded-full bg-ink px-3 text-xs font-semibold text-ground shadow-lg transition-opacity hover:opacity-90"
					>
						<ChevronUp className="size-3.5" aria-hidden />
						{pending.length} new {pending.length === 1 ? "event" : "events"}
					</button>
				</div>
			)}

			{loading && <SkeletonList rows={10} rowHeight={44} />}

			{!loading && events.length === 0 && (
				<EmptyState
					icon={Activity}
					title="No events yet."
					description='Tap "Debug event" to verify the pipe end-to-end.'
				/>
			)}

			{!loading && events.length > 0 && filtered.length === 0 && (
				<EmptyState icon={Filter} title="No events match the active filters." />
			)}

			{/* @container: FeedItem lays itself out by this list's width. */}
			<ul
				ref={listRef}
				className={`@container divide-y divide-hairline overflow-hidden rounded-[10px] bg-paper ${
					filtered.length > 0 ? "border border-hairline" : ""
				}`}
			>
				{filtered.map((e) => (
					<FeedItem key={e.id} event={e} isFresh={freshIds.has(e.id)} />
				))}
			</ul>

			{!loading && hasMore && events.length > 0 && (
				<div className="mt-3 flex justify-center">
					<Button variant="secondary" size="sm" onClick={loadOlder} disabled={loadingMore} className="gap-1.5">
						<RotateCw className={`size-3.5 ${loadingMore ? "animate-spin" : ""}`} aria-hidden />
						{loadingMore ? "Loading…" : "Load older"}
					</Button>
				</div>
			)}
			{!loading && !hasMore && events.length > 0 && (
				<p className="mt-3 text-center text-xs text-muted">That's all the history.</p>
			)}
		</section>
	);
}
